// Wave 14+ / Rapha Fit — measurement + size + inline compression view.
//
// Shows the rider's measured circumferences + the recommended size per
// garment category. Clicking a garment row opens the inline compression
// view (CompressionMap component) for that garment + size.

import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { useTranslation } from 'react-i18next';
import { colors } from '../theme';
import CompressionMap from '../components/CompressionMap';
import TryOnView from '../components/TryOnView';
import FitHistory from './FitHistory';

interface Profile {
  heightCm: number | null;
  chestCm: number | null;
  waistCm: number | null;
  hipCm: number | null;
  thighCm: number | null;
  shoulderWidthCm: number | null;
  torsoLengthCm: number | null;
  upperArmCm: number | null;
  circumferenceSeries: Array<{ y_cm: number; circumference_cm: number }>;
  recommendedSizes: Record<string, string>;
}

interface Recommendation {
  garmentId: string;
  category: string;
  garmentName: string;
  recommendedSize: string | null;
  score: number;
}

interface HullMesh {
  positions: Float32Array;
  indices: Uint16Array | Uint32Array;
  normals: Float32Array;
}

interface Props {
  profile: Profile;
  recommendations: Recommendation[];
  hullMesh?: HullMesh | null;
  onRemeasure: () => void;
}

const MEASUREMENT_KEYS: Array<keyof Profile> = [
  'heightCm',
  'chestCm',
  'waistCm',
  'hipCm',
  'thighCm',
];

const LABEL_KEY: Record<string, string> = {
  heightCm: 'height',
  chestCm: 'chest',
  waistCm: 'waist',
  hipCm: 'hip',
  thighCm: 'thigh',
  shoulderWidthCm: 'shoulder',
  torsoLengthCm: 'torso',
  upperArmCm: 'upperArm',
};

export default function FitReport({ profile, recommendations, hullMesh, onRemeasure }: Props) {
  const { t } = useTranslation('rapha-fit');
  const [picked, setPicked] = useState<{ garmentId: string; size: string } | null>(null);
  // 'compression' vs 'tryOn' — both views share the picked garment/size.
  const [mode, setMode] = useState<'compression' | 'tryOn'>('compression');

  // Group recommendations by category for the size cards.
  const byCategory = useMemo(() => {
    const m = new Map<string, Recommendation[]>();
    for (const r of recommendations) {
      if (!m.has(r.category)) m.set(r.category, []);
      m.get(r.category)!.push(r);
    }
    return Array.from(m.entries());
  }, [recommendations]);

  return (
    <Stack spacing={4}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {t('report.title')}
        </Typography>
        <Button onClick={onRemeasure} variant="outlined" startIcon={<RotateRightIcon />} sx={{ color: colors.brand, borderColor: colors.borderFocus }}>
          {t('report.remeasure')}
        </Button>
      </Stack>

      {/* Measurements */}
      <Box sx={{ p: 3, borderRadius: 3, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted, mb: 2 }}>
          {t('report.measurements')}
        </Typography>
        <Grid container spacing={2}>
          {MEASUREMENT_KEYS.map((k) => {
            const v = profile[k] as number | null;
            return (
              <Grid item xs={6} sm={4} md={2.4} key={k as string}>
                <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted, mb: 0.5 }}>
                  {t(`labels.${LABEL_KEY[k as string]}`)}
                </Typography>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '1.5rem', fontWeight: 700, color: colors.brand }}>
                  {v != null ? `${v.toFixed(1)} cm` : '—'}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Recommended sizes — one card per category */}
      <Box>
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted, mb: 2 }}>
          {t('report.recommendedSizes')}
        </Typography>
        <Grid container spacing={2}>
          {byCategory.map(([category, items]) => (
            <Grid item xs={12} md={6} key={category}>
              <Box sx={{ p: 2.5, borderRadius: 3, backgroundColor: colors.surface, border: `1px solid ${colors.border}`, height: '100%' }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.brand, mb: 1.25 }}>
                  {t(`categories.${category}`)}
                </Typography>
                <Stack spacing={1}>
                  {items.map((r) => (
                    <Box
                      key={r.garmentId}
                      onClick={() => r.recommendedSize && setPicked({ garmentId: r.garmentId, size: r.recommendedSize })}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: colors.surfaceElevated,
                        border: `1px solid ${
                          picked?.garmentId === r.garmentId ? colors.brand : colors.border
                        }`,
                        cursor: r.recommendedSize ? 'pointer' : 'default',
                        '&:hover': r.recommendedSize ? { borderColor: colors.borderFocus } : {},
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ color: colors.text, fontWeight: 600 }}>
                        {r.garmentName}
                      </Typography>
                      <Chip
                        label={r.recommendedSize ?? t('report.noSize')}
                        size="small"
                        sx={{
                          backgroundColor: r.recommendedSize ? 'rgba(106, 255, 69, 0.12)' : 'transparent',
                          border: `1px solid ${r.recommendedSize ? colors.borderFocus : colors.border}`,
                          color: r.recommendedSize ? colors.brand : colors.textMuted,
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Inline compression / try-on view for the selected garment + size */}
      {picked && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <Chip
              label={t('compression.title')}
              onClick={() => setMode('compression')}
              sx={{
                cursor: 'pointer',
                backgroundColor: mode === 'compression' ? 'rgba(106, 255, 69, 0.12)' : colors.surfaceElevated,
                border: `1px solid ${mode === 'compression' ? colors.brand : colors.border}`,
                color: mode === 'compression' ? colors.brand : colors.text,
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            />
            <Chip
              label={t('tryOn.title')}
              onClick={() => setMode('tryOn')}
              sx={{
                cursor: 'pointer',
                backgroundColor: mode === 'tryOn' ? 'rgba(106, 255, 69, 0.12)' : colors.surfaceElevated,
                border: `1px solid ${mode === 'tryOn' ? colors.brand : colors.border}`,
                color: mode === 'tryOn' ? colors.brand : colors.text,
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            />
          </Stack>
          {mode === 'compression' ? (
            <CompressionMap
              garmentId={picked.garmentId}
              size={picked.size}
              hullMesh={hullMesh ?? null}
              riderHeightCm={profile.heightCm ?? null}
              onChangeSize={(size) => setPicked({ garmentId: picked.garmentId, size })}
              onClose={() => setPicked(null)}
            />
          ) : (
            <TryOnView
              garmentId={picked.garmentId}
              size={picked.size}
              hullMesh={hullMesh ?? null}
              riderHeightCm={profile.heightCm ?? null}
              onChangeSize={(size) => setPicked({ garmentId: picked.garmentId, size })}
              onClose={() => setPicked(null)}
            />
          )}
        </Stack>
      )}

      {/* Remeasure history sparklines — needs at least two rows to render. */}
      <FitHistory />
    </Stack>
  );
}
